"use client";

import { ArrowRight, Check } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/_utils";
import { MONTHLY_FEATURES, SESSION_FEATURES } from "./data";

type PricingMode = "session" | "monthly";

const PAYMENT_ICONS = [
  { src: "/images/creditcards/mastercard.svg", alt: "Mastercard" },
  { src: "/images/creditcards/visa.svg", alt: "Visa" },
  { src: "/images/creditcards/amex.svg", alt: "American Express" },
  { src: "/images/creditcards/discover.svg", alt: "Discover" },
  // { src: "/images/creditcards/jcb.svg", alt: "JCB" },
  // { src: "/images/creditcards/diners.svg", alt: "Diners Club" },
  // { src: "/images/creditcards/elo.svg", alt: "Elo" },
  // { src: "/images/creditcards/paypal.svg", alt: "UnionPay" },
] as const;

export function HomeV2Pricing() {
  const [mode, setMode] = useState<PricingMode>("session");
  const isSession = mode === "session";
  const features = isSession ? SESSION_FEATURES : MONTHLY_FEATURES;

  return (
    <section className="bg-white px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
      <div className="mx-auto flex max-w-[1400px] flex-col items-center">
        {/* Header */}
        <div className="mx-auto max-w-[1056px] text-center">
          <h2 className="font-bold text-[#2C2C2C] text-[56px] leading-[1.1] tracking-[-0.02em] max-lg:text-4xl">
            Simple, transparent pricing
          </h2>
          <div aria-hidden className="h-6" />
          <p className="mx-auto max-w-[989px] font-normal text-[#3F474E] text-xl leading-[1.4]">
            Get expert trade guidance on demand. Pay per session or choose a
            monthly plan for ongoing support.
          </p>
        </div>

        <div aria-hidden className="h-12" />

        {/* Toggle — label | switch | label */}
        <div className="flex items-center gap-[9px]">
          <span
            className={cn(
              "text-lg leading-[1.4]",
              isSession
                ? "font-bold text-[#2C2C2C]"
                : "font-normal text-[#3F474E]"
            )}
          >
            Pay per session
          </span>
          <button
            aria-checked={!isSession}
            aria-label="Toggle monthly plans"
            className="relative flex h-8 w-[60px] shrink-0 items-start rounded-[20px] bg-[#9FB3C8] p-0.5 transition-colors"
            onClick={() => setMode(isSession ? "monthly" : "session")}
            role="switch"
            type="button"
          >
            <span
              className={cn(
                "size-7 rounded-full bg-white shadow-[0px_4px_8px_-2px_rgba(16,24,40,0.1),0px_2px_4px_-2px_rgba(16,24,40,0.06)] transition-transform",
                isSession ? "translate-x-0" : "translate-x-[28px]"
              )}
            />
          </button>
          <span
            className={cn(
              "text-lg leading-[1.4]",
              isSession
                ? "font-normal text-[#3F474E]"
                : "font-bold text-[#2C2C2C]"
            )}
          >
            Monthly plans
          </span>
        </div>

        <div aria-hidden className="h-2" />

        <p className="max-w-[538px] text-center font-medium text-[#3F474E] text-sm leading-[1.4]">
          Session pricing is ideal for quick fixes. Monthly plans are best for
          ongoing projects.
        </p>

        <div aria-hidden className="h-10" />

        {/* Pricing card */}
        <div className="flex w-full flex-col gap-10 rounded-[40px] border border-[#BCCAD4] p-4 sm:p-6 lg:flex-row lg:items-start lg:gap-12 lg:py-2.5 lg:pr-2.5 lg:pl-20">
          {/* Left content */}
          <div className="flex min-w-0 flex-1 flex-col lg:max-w-[920px]">
            <div aria-hidden className="hidden h-12 lg:block" />

            <p className="font-bold text-base text-tt-blue uppercase leading-none tracking-[0.2em]">
              {isSession
                ? "Pay only when you need help"
                : "Best for ongoing work"}
            </p>

            <div aria-hidden className="h-4" />

            <h3 className="font-bold text-[#2C2C2C] text-[40px] leading-[1.2] max-sm:text-3xl">
              {isSession ? "On-demand sessions" : "Monthly support"}
            </h3>

            <div aria-hidden className="h-6" />

            <p className="font-normal text-[#3F474E] text-xl leading-[1.4]">
              {isSession
                ? "Book qualified tradespeople starting from 15-minute sessions. Perfect for quick troubleshooting, second opinions, and project planning."
                : "Stay connected with tradies throughout your project. Ideal when you need recurring guidance across multiple jobs."}
            </p>

            <div aria-hidden className="h-10" />

            <div className="flex items-center gap-6">
              <p className="shrink-0 font-bold text-[#0586D7] text-xl leading-[1.4]">
                {isSession ? "Pay per session" : "Monthly plan"}
              </p>
              <div className="h-px flex-1 bg-[#BCCAD4]" />
            </div>

            <div aria-hidden className="h-6" />

            <ul className="grid gap-x-2 gap-y-5 sm:grid-cols-2">
              {features.map((feature) => (
                <li className="flex items-start gap-3" key={feature}>
                  <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-[#02A8EA]/20 text-[#02A8EA]">
                    <Check className="size-4 stroke-[3]" />
                  </span>
                  <span className="whitespace-nowrap font-normal text-[#3F474E] text-xl leading-[1.4]">
                    {feature}
                  </span>
                </li>
              ))}
            </ul>

            <div aria-hidden className="hidden h-12 lg:block" />
          </div>

          {/* Right price panel */}
          <div className="flex w-full flex-col items-center justify-center rounded-[30px] bg-[#F5F5F5] px-8 py-10 sm:px-12 lg:w-[450px] lg:shrink-0 lg:px-20 lg:py-10">
            <div className="flex flex-col items-center">
              <p className="font-bold text-7xl text-[#0586D7] leading-[1.1] tracking-[-0.02em]">
                {isSession ? "$29" : "$79"}
              </p>
              <div aria-hidden className="h-2" />
              <p className="font-bold text-[#3F474E] text-lg leading-[1.4]">
                {isSession ? "USD per 15 mins" : "USD per month"}
              </p>
            </div>

            <div aria-hidden className="h-12" />

            <Button
              asChild
              className="h-[45px] min-w-[130px] rounded-full bg-tt-yellow px-6 font-semibold text-[#2C2C2C] text-lg leading-[1.4] hover:bg-tt-yellow/90"
            >
              <Link href="/v2/login">
                Teletradie now!
                <ArrowRight className="size-4" />
              </Link>
            </Button>

            <div aria-hidden className="h-6" />

            <p className="max-w-[290px] text-center font-medium text-[#3F474E] text-sm leading-[1.4]">
              Session pricing is ideal for quick fixes. Monthly plans are best
              for ongoing projects.
            </p>

            <div aria-hidden className="h-12" />

            <div className="flex max-w-[380px] flex-wrap items-center justify-center gap-2">
              {PAYMENT_ICONS.map((icon) => (
                <div
                  className="relative h-6 w-[39px] overflow-hidden rounded border border-[#D6D6D6] bg-white"
                  key={icon.alt}
                >
                  <Image
                    alt={icon.alt}
                    className="object-contain p-0.5"
                    fill
                    sizes="39px"
                    src={icon.src}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
